<template>
  <div class="image-upload-with-crop">
    <label v-if="label" class="upload-label">{{ label }}</label>
    
    <!-- Preview da imagem cropada -->
    <div 
      v-if="croppedImage"
      class="upload-area upload-area--has-image"
      @click="!readonly && triggerFileInput()"
    >
      <v-img
        :src="croppedImage"
        :aspect-ratio="1"
        contain
        class="preview-img"
      />
      
      <div v-if="!readonly" class="image-overlay">
        <v-btn
          icon
          small
          color="gray"
          class="overlay-btn"
          @click.stop="removeImage"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          color="gray"
          class="overlay-btn"
          @click.stop="triggerFileInput"
        >
          <v-icon small>mdi-swap-horizontal</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Estado Vazio -->
    <div 
      v-else
      class="upload-area"
      :class="{ 'upload-area--error': errorMessage }"
      @click="!readonly && triggerFileInput()"
    >
      <div class="upload-placeholder">
        <v-icon size="48" color="grey lighten-1">mdi-image-plus</v-icon>
        <p class="placeholder-text">Clique para adicionar imagem</p>
        <p class="placeholder-hint">PNG, JPG até {{ maxSizeMB }}MB</p>
      </div>
    </div>

    <!-- Input escondido -->
    <input
      ref="fileInput"
      type="file"
      accept="image/png, image/jpeg, image/jpg"
      @change="handleFileSelect"
      style="display: none"
    />

    <!-- Mensagem de Erro -->
    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      text
      class="mt-2"
      dismissible
      @input="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- Dialog do Cropper -->
    <v-dialog v-model="cropDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">
          Ajustar Imagem
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCropDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <div class="crop-container">
            <img ref="cropperImage" :src="tempImageSrc" style="max-width: 100%; display: block;" />
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text @click="closeCropDialog">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="applyCrop" :loading="loading">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

export default {
  name: 'ImageUploadWithCrop',

  props: {
    value: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: 'Imagem'
    },
    maxSizeMB: {
      type: Number,
      default: 5
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      croppedImage: this.value || null,
      tempImageSrc: null,
      cropDialog: false,
      errorMessage: '',
      loading: false,
      cropper: null,
      selectedFile: null
    }
  },

  watch: {
    value(newVal) {
      this.croppedImage = newVal
    }
  },

  beforeDestroy() {
    if (this.cropper) {
      this.cropper.destroy()
    }
  },

  methods: {
    triggerFileInput() {
      if (!this.readonly) {
        this.$refs.fileInput.click()
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
      
      event.target.value = ''
    },

    async processFile(file) {
      this.errorMessage = ''

      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Apenas arquivos JPG e PNG são permitidos'
        return
      }

      
      const maxSizeBytes = this.maxSizeMB * 1024 * 1024
      if (file.size > maxSizeBytes) {
        this.errorMessage = `O arquivo deve ter no máximo ${this.maxSizeMB}MB`
        return
      }

      
      this.selectedFile = file

      
      const reader = new FileReader()
      reader.onload = (e) => {
        this.tempImageSrc = e.target.result
        this.openCropDialog()
      }
      reader.readAsDataURL(file)
    },

    openCropDialog() {
      this.cropDialog = true
      
      
      this.$nextTick(() => {
        this.initCropper()
      })
    },

    initCropper() {
      if (this.cropper) {
        this.cropper.destroy()
      }

      const image = this.$refs.cropperImage
      this.cropper = new Cropper(image, {
        aspectRatio: 1, 
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 0.8,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        responsive: true,
        background: false,
        modal: true,
      })
    },

    closeCropDialog() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      this.cropDialog = false
      this.tempImageSrc = null
    },

    applyCrop() {
      if (!this.cropper) return

      this.loading = true

      try {
        
        const canvas = this.cropper.getCroppedCanvas({
          width: 300,
          height: 300,
          imageSmoothingQuality: 'high'
        })

        
        const croppedBase64 = canvas.toDataURL('image/jpeg', 0.9)
        
        
        canvas.toBlob((blob) => {
          const croppedFile = new File([blob], this.selectedFile.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          })

          
          this.croppedImage = croppedBase64

          
          this.$emit('input', croppedBase64)
          this.$emit('file-selected', {
            file: croppedFile,
            base64: croppedBase64
          })

          
          this.closeCropDialog()
          this.loading = false
        }, 'image/jpeg', 0.9)

      } catch (error) {
        console.error('Erro ao aplicar crop:', error)
        this.errorMessage = 'Erro ao processar imagem'
        this.loading = false
      }
    },

    removeImage() {
      if (this.readonly) return
      
      this.croppedImage = null
      this.selectedFile = null
      this.$emit('input', null)
      this.$emit('image-removed')
    }
  }
}
</script>

<style scoped>
.image-upload-with-crop {
  width: 100%;
}

.upload-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 8px;
}

.upload-area {
  position: relative;
  width: 100%;
  max-width: 300px;
  min-height: 180px;
  border: 2px dashed #d0d0d0;
  border-radius: 12px;
  background-color: #fafafa;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover:not(.upload-area--has-image) {
  border-color: #1976d2;
  background-color: #f5f5f5;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.upload-area--has-image {
  border-style: solid;
  border-color: #4caf50;
  background-color: white;
  padding: 0;
  min-height: 180px;
}

.upload-area--has-image:hover {
  border-color: #1976d2;
}

.upload-area--error {
  border-color: #f44336;
}

.upload-placeholder {
  text-align: center;
  padding: 24px;
}

.placeholder-text {
  margin: 16px 0 4px 0;
  font-size: 16px;
  font-weight: 500;
  color: #424242;
}

.placeholder-hint {
  margin: 0;
  font-size: 12px;
  color: #757575;
}

.image-preview {
  width: 100%;
  height: 100%;
  position: relative;
}

.preview-img {
  width: 100%;
  height: 100%;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.upload-area--has-image:hover .image-overlay {
  opacity: 1;
}

.overlay-btn {
  background-color: rgba(255, 255, 255, 0.9) !important;
}

.crop-container {
  max-height: 400px;
  overflow: hidden;
}

.crop-container img {
  max-width: 100%;
  display: block;
}
</style>
